import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, Upload, X } from 'lucide-react';
import { useTheme } from '../../../components/common/Theme';
import { roleService } from '../../../components/api/roleService';
import FilterableMultiSelect from '../common/FilterableMultiSelect';
import { CatalogAppFormData, CatalogAvailability, CatalogStatus } from './catalogTypes';
import { CATALOG_ICON_OPTIONS } from './catalogIcons';
import CatalogConfirmDialog from './CatalogConfirmDialog';
import { catalogService } from './catalogService';
import { logCatalog } from './catalogLogger';

const APP_CODE_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const APP_CODE_LEN = 5;

function generateAppCode(existingCodes: string[]): string {
  const taken = new Set(existingCodes.map((c) => c.toUpperCase()));
  for (let attempt = 0; attempt < 40; attempt += 1) {
    let code = '';
    for (let i = 0; i < APP_CODE_LEN; i += 1) {
      code += APP_CODE_ALPHABET[Math.floor(Math.random() * APP_CODE_ALPHABET.length)];
    }
    if (!taken.has(code)) return code;
  }
  return Array.from({ length: APP_CODE_LEN }, () =>
    APP_CODE_ALPHABET[Math.floor(Math.random() * APP_CODE_ALPHABET.length)]
  ).join('');
}

interface RegisterCatalogAppModalProps {
  isOpen: boolean;
  existingCodes: string[];
  editMode?: boolean;
  initialData?: CatalogAppFormData | null;
  onClose: () => void;
  onSave: (data: CatalogAppFormData) => void;
}

interface OwnerOption {
  userId: string;
  label: string;
}

const EMPTY_FORM: CatalogAppFormData = {
  name: '',
  description: '',
  icon: 'Boxes',
  iconColor: 'indigo',
  iconUrl: '',
  dns: '',
  url: '',
  status: 'active',
  appCode: '',
  ownerId: '',
  availability: 'both',
  copilotIds: [],
};

const AVAILABILITY_OPTIONS: { value: CatalogAvailability; label: string }[] = [
  { value: 'copilot', label: 'Copilot' },
  { value: 'standalone', label: 'Standalone' },
  { value: 'both', label: 'Both' },
];

const STATUS_OPTIONS: { value: CatalogStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'maintenance', label: 'Maintenance' },
];

const MAX_ICON_BYTES = 500 * 1024;

function flattenOwners(roles: unknown): OwnerOption[] {
  if (!Array.isArray(roles)) return [];
  const byId = new Map<string, OwnerOption>();
  for (const role of roles) {
    const assigned = (role as { assignedUsers?: unknown }).assignedUsers;
    if (!Array.isArray(assigned)) continue;
    for (const user of assigned) {
      const raw = user as {
        userId?: string;
        email?: string;
        firstName?: string;
        lastName?: string;
      };
      const userId = String(raw.userId ?? '').trim();
      if (!userId || byId.has(userId)) continue;
      const name = `${raw.firstName ?? ''} ${raw.lastName ?? ''}`.trim() || 'User';
      const email = String(raw.email ?? '').trim();
      byId.set(userId, {
        userId,
        label: email ? `${name} (${email})` : name,
      });
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
}

const RegisterCatalogAppModal: React.FC<RegisterCatalogAppModalProps> = ({
  isOpen,
  existingCodes,
  editMode = false,
  initialData = null,
  onClose,
  onSave,
}) => {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const [form, setForm] = useState<CatalogAppFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [owners, setOwners] = useState<OwnerOption[]>([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [copilotOptions, setCopilotOptions] = useState<{ label: string; value: string }[]>([]);
  const [copilotsLoading, setCopilotsLoading] = useState(false);
  const [iconError, setIconError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const next = initialData
      ? {
          ...EMPTY_FORM,
          ...initialData,
          iconColor: 'indigo',
          iconUrl: initialData.iconUrl || '',
          copilotIds: Array.isArray(initialData.copilotIds) ? initialData.copilotIds : [],
        }
      : EMPTY_FORM;
    setForm(next);
    setErrors({});
    setShowConfirm(false);
    setIconError(null);
  }, [isOpen, initialData]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadOwners = async () => {
      setOwnersLoading(true);
      try {
        const response = await roleService.getRolesWithUsers();
        if (!cancelled) {
          setOwners(flattenOwners(response));
          logCatalog('app', 'catalog_owners_loaded', {
            count: Array.isArray(response) ? response.length : 0,
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load owners';
        logCatalog('error', 'catalog_owners_load_failed', { message });
        if (!cancelled) setOwners([]);
      } finally {
        if (!cancelled) setOwnersLoading(false);
      }
    };

    const loadCopilots = async () => {
      setCopilotsLoading(true);
      try {
        const items = await catalogService.listCopilots();
        if (!cancelled) {
          setCopilotOptions(
            items.map((c) => ({
              value: c.agentId,
              label: c.agentTitle || c.agentId,
            }))
          );
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load copilots';
        logCatalog('error', 'catalog_copilots_load_failed', { message });
        if (!cancelled) setCopilotOptions([]);
      } finally {
        if (!cancelled) setCopilotsLoading(false);
      }
    };

    void loadOwners();
    void loadCopilots();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const ownerSelectOptions = useMemo(() => {
    if (!form.ownerId) return owners;
    if (owners.some((o) => o.userId === form.ownerId)) return owners;
    return [
      ...owners,
      {
        userId: form.ownerId,
        label: `${form.ownerId} (current)`,
      },
    ];
  }, [owners, form.ownerId]);

  const showCopilotPicker = form.availability === 'copilot' || form.availability === 'both';

  if (!isOpen) return null;

  const inputClass = `w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
    isLight
      ? 'bg-white border border-neutral-300 text-neutral-900 placeholder-neutral-400'
      : 'bg-neutral-800 border border-neutral-700 text-white placeholder-gray-500'
  }`;

  const labelClass = `block text-xs font-medium mb-1.5 ${isLight ? 'text-neutral-600' : 'text-gray-400'}`;

  const update = <K extends keyof CatalogAppFormData>(key: K, value: CatalogAppFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleIconUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setIconError('Please choose an image file.');
      return;
    }
    if (file.size > MAX_ICON_BYTES) {
      setIconError('Icon must be 500KB or smaller.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!result) {
        setIconError('Could not read icon file.');
        return;
      }
      setIconError(null);
      update('iconUrl', result);
      logCatalog('app', 'catalog_icon_uploaded', { size: file.size, type: file.type });
    };
    reader.onerror = () => setIconError('Could not read icon file.');
    reader.readAsDataURL(file);
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Name is required.';
    if (!form.url.trim()) next.url = 'URL is required.';
    else if (!/^https?:\/\/.+/i.test(form.url.trim())) next.url = 'URL must start with http:// or https://';
    if (!form.appCode.trim()) next.appCode = 'App code is required.';
    else if (existingCodes.includes(form.appCode.trim().toUpperCase())) next.appCode = 'App code must be unique.';
    if (!form.dns.trim()) next.dns = 'DNS is required.';
    if (!form.ownerId.trim()) next.ownerId = 'Owner is required.';
    setErrors(next);
    if (Object.keys(next).length > 0) {
      logCatalog('warning', 'register_validation_failed', { fields: Object.keys(next) });
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    onSave({
      ...form,
      name: form.name.trim(),
      description: form.description.trim(),
      dns: form.dns.trim(),
      url: form.url.trim(),
      appCode: form.appCode.trim().toUpperCase(),
      ownerId: form.ownerId.trim(),
      iconColor: 'indigo',
      iconUrl: form.iconUrl || '',
      copilotIds: Array.isArray(form.copilotIds) ? form.copilotIds : [],
    });
    setShowConfirm(false);
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[140] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <div
          className={`w-full max-w-2xl max-h-[92vh] overflow-hidden rounded-xl border shadow-2xl flex flex-col ${
            isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'
          }`}
        >
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
            <div>
              <h2 className={`text-lg font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                {editMode ? 'Edit catalog app' : 'Register catalog app'}
              </h2>
              <p className={`text-xs mt-0.5 ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
                {editMode
                  ? 'Update this catalog application. You will confirm before changes are saved.'
                  : 'Add a named application to the catalog. You will confirm before it is saved.'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900' : 'text-gray-400 hover:bg-neutral-800 hover:text-white'}`}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={labelClass}>Nice name</label>
                <input
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  className={inputClass}
                  placeholder="e.g. Selfic Copilot"
                />
                {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                  rows={3}
                  className={inputClass}
                  placeholder="What this application does"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={`text-xs font-medium ${isLight ? 'text-neutral-600' : 'text-gray-400'}`}>
                    App code
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const code = generateAppCode(existingCodes);
                      update('appCode', code);
                      logCatalog('app', 'catalog_app_code_generated', { appCode: code });
                    }}
                    className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                      isLight
                        ? 'text-indigo-600 hover:text-indigo-800'
                        : 'text-indigo-400 hover:text-indigo-300'
                    }`}
                    title="Generate a 5-character alphanumeric code"
                  >
                    <RefreshCw size={11} />
                    Generate
                  </button>
                </div>
                <input
                  value={form.appCode}
                  onChange={(e) =>
                    update(
                      'appCode',
                      e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, APP_CODE_LEN).toUpperCase()
                    )
                  }
                  className={inputClass}
                  placeholder="e.g. A3K9P"
                  maxLength={APP_CODE_LEN}
                  autoComplete="off"
                />
                {errors.appCode && <p className="text-xs text-red-400 mt-1">{errors.appCode}</p>}
              </div>

              <div>
                <label className={labelClass}>Owner</label>
                <select
                  value={form.ownerId}
                  onChange={(e) => update('ownerId', e.target.value)}
                  className={inputClass}
                  disabled={ownersLoading}
                >
                  <option value="">{ownersLoading ? 'Loading users…' : 'Select owner'}</option>
                  {ownerSelectOptions.map((opt) => (
                    <option key={opt.userId} value={opt.userId}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.ownerId && <p className="text-xs text-red-400 mt-1">{errors.ownerId}</p>}
              </div>

              <div>
                <label className={labelClass}>DNS</label>
                <input
                  value={form.dns}
                  onChange={(e) => update('dns', e.target.value)}
                  className={inputClass}
                  placeholder="app.selfic.ai"
                />
                {errors.dns && <p className="text-xs text-red-400 mt-1">{errors.dns}</p>}
              </div>

              <div>
                <label className={labelClass}>URL</label>
                <input
                  value={form.url}
                  onChange={(e) => update('url', e.target.value)}
                  className={inputClass}
                  placeholder="https://..."
                />
                {errors.url && <p className="text-xs text-red-400 mt-1">{errors.url}</p>}
              </div>

              <div>
                <label className={labelClass}>Status</label>
                <select
                  value={form.status}
                  onChange={(e) => update('status', e.target.value as CatalogStatus)}
                  className={inputClass}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Availability</label>
                <div className={`flex rounded-lg border p-0.5 ${isLight ? 'border-neutral-300 bg-neutral-50' : 'border-neutral-700 bg-neutral-800'}`}>
                  {AVAILABILITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => update('availability', opt.value)}
                      className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        form.availability === opt.value
                          ? 'bg-indigo-600 text-white'
                          : isLight
                            ? 'text-neutral-600 hover:text-neutral-900'
                            : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {showCopilotPicker && (
              <div>
                {copilotsLoading ? (
                  <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>Loading copilots…</p>
                ) : (
                  <FilterableMultiSelect
                    label="Copilots"
                    options={copilotOptions}
                    selected={form.copilotIds}
                    setSelected={(values) => update('copilotIds', values)}
                  />
                )}
              </div>
            )}

            <div>
              <label className={labelClass}>Icon</label>
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`h-12 w-12 flex-shrink-0 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden ${
                    isLight ? 'border-neutral-300 bg-neutral-50' : 'border-neutral-700 bg-neutral-800'
                  }`}
                >
                  {form.iconUrl ? (
                    <img src={form.iconUrl} alt="App icon" className="w-full h-full object-cover" />
                  ) : (
                    <span className={`text-xs ${isLight ? 'text-neutral-400' : 'text-gray-500'}`}>Preview</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <label
                    className={`cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border ${
                      isLight
                        ? 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                        : 'bg-neutral-800 border-neutral-700 text-gray-300 hover:bg-neutral-700'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Upload icon
                    <input type="file" accept="image/*" onChange={handleIconUpload} className="hidden" />
                  </label>
                  {form.iconUrl && (
                    <button
                      type="button"
                      onClick={() => {
                        update('iconUrl', '');
                        setIconError(null);
                      }}
                      className={`text-xs ${isLight ? 'text-neutral-500 hover:text-red-500' : 'text-gray-400 hover:text-red-400'}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              {iconError && <p className="text-xs text-red-400 mb-2">{iconError}</p>}
              <p className={`text-xs mb-2 ${isLight ? 'text-neutral-500' : 'text-gray-500'}`}>
                Or choose a preset
              </p>
              <div className="grid grid-cols-7 gap-2">
                {CATALOG_ICON_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const selected = !form.iconUrl && form.icon === opt.name;
                  return (
                    <button
                      key={opt.name}
                      type="button"
                      title={opt.label}
                      onClick={() => update('icon', opt.name)}
                      className={`flex items-center justify-center h-9 rounded-lg border transition-colors ${
                        selected
                          ? 'border-indigo-500 bg-indigo-600/15 text-indigo-400'
                          : isLight
                            ? 'border-neutral-200 text-neutral-500 hover:border-neutral-300 hover:bg-neutral-50'
                            : 'border-neutral-700 text-gray-400 hover:border-neutral-600 hover:bg-neutral-800'
                      }`}
                    >
                      <Icon size={16} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={`flex justify-end gap-2 pt-2 border-t ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 rounded-lg text-sm ${isLight ? 'text-neutral-600 hover:bg-neutral-100' : 'text-gray-300 hover:bg-neutral-800'}`}
              >
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
                Continue
              </button>
            </div>
          </form>
        </div>
      </div>

      {showConfirm && (
        <CatalogConfirmDialog
          title={editMode ? 'Save these changes?' : 'Register this app?'}
          message={
            editMode
              ? `Update “${form.name.trim()}” (${form.appCode.trim().toUpperCase()}) in the catalog?`
              : `Add “${form.name.trim()}” (${form.appCode.trim().toUpperCase()}) to the catalog?`
          }
          confirmLabel={editMode ? 'Yes, save' : 'Yes, register'}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>,
    document.body
  );
};

export default RegisterCatalogAppModal;
